import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TmBaseComponent } from '@treasury/presentation';
import { TmForm, TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-date-range';

import { DeepReactive } from '@jack-henry/frontend-utils/lit';
// eslint-disable-next-line no-duplicate-imports
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-select';
import '@vaadin/form-layout';
// eslint-disable-next-line no-duplicate-imports
import type { FormLayoutResponsiveStep } from '@vaadin/form-layout';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { ReportFiltersVm, mapAccountToVm } from '../data/report-filters.vm';

type FormModel = Pick<
    ReportFiltersVm,
    'accountTypes' | 'accounts' | 'sortBy' | 'isAscendingSort'
> & { dateRange: DateRange };

const tagName = 'balance-report-filters';
@customElement(tagName)
export class BalanceReportFilters extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public range = false;

    @DeepReactive()
    public filters!: ReportFiltersVm;

    private readonly form = createRef<TmForm<FormModel>>();

    private readonly responsiveSteps: FormLayoutResponsiveStep[] = [
        { minWidth: 0, columns: 1 },
        { minWidth: '768px', columns: 2 },
        { minWidth: '1024px', columns: 3 },
    ];

    public render() {
        if (!this.filters) {
            return nothing;
        }

        return html`
            <tm-form
                ${ref(this.form)}
                .validator=${() => this.filters.isValid}
                @form-changed=${() => {
                    this.dispatchFiltersChanged();
                }}
            >
                <vaadin-form-layout .responsiveSteps=${this.responsiveSteps}>
                    ${this.range
                        ? html`<tm-date-range
                              name="dateRange"
                              label="Date"
                              required
                              .initial=${this.filters.initialDateRange}
                              .maxDate=${new Date()}
                              @value-changed=${(e: TmValueChanged<DateRange>) => {
                                  const { value } = e.detail;
                                  if (value) {
                                      this.filters.dateType = value.rangeType;
                                      this.filters.fromDate = value.startDate
                                          ? format(value.startDate, 'P')
                                          : '';
                                      this.filters.toDate = value.endDate
                                          ? format(value.endDate, 'P')
                                          : '';
                                      this.filters.specificDate =
                                          value.startDate &&
                                          value.rangeType === DateRangeType.SpecificDate
                                              ? format(value.startDate, 'P')
                                              : '';
                                  }
                              }}
                          ></tm-date-range>`
                        : nothing}
                    <tm-select
                        name="accountTypes"
                        label="Account Type"
                        .initial=${this.filters.accountTypesString}
                        .items=${this.filters.accountTypeItemsString}
                        @value-changed=${(e: CustomEvent) => {
                            if (e.detail.value) {
                                this.filters.accountTypes = e.detail.value;
                                this.requestUpdate();
                            }
                        }}
                    ></tm-select>
                    <tm-multi-select
                        name="accounts"
                        label="Accounts"
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Accounts'}
                        .items=${this.filters.filteredAccounts}
                        .initial=${this.filters.selectedAccounts}
                        .mapItemToVm=${mapAccountToVm}
                        @value-changed=${(e: TmValueChanged<CompanyAccount[]>) => {
                            if (!e.detail.value) {
                                return;
                            }
                            this.filters.accounts = e.detail.value;
                        }}
                    ></tm-multi-select>
                    <div>
                        <tm-select
                            name="sortBy"
                            label="Sort By"
                            .initial=${this.filters.sortByInitial}
                            .items=${this.filters.sortByItems}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value) {
                                    this.filters.sortBy = e.detail.value;
                                }
                            }}
                        ></tm-select>
                        <tm-radio-group
                            name="isAscendingSort"
                            .initial=${this.filters.displayIsAscendingSort}
                            .options=${this.filters.isAscendingSortOptions}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value) {
                                    this.filters.displayIsAscendingSort = e.detail.value;
                                }
                            }}
                        ></tm-radio-group>
                    </div>
                </vaadin-form-layout>
            </tm-form>
        `;
    }

    protected firstUpdated(): void {
        this.dispatchFiltersChanged();
    }

    private dispatchFiltersChanged() {
        this.dispatchEvent(
            new CustomEvent('filters-changed', {
                detail: {
                    filters: this.filters,
                    isValid: this.form.value?.isValid ?? false,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: BalanceReportFilters;
    }
}
